import * as React from 'react';
import { cn } from '../utils';

export function Badge({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border border-border bg-muted px-1.5 py-px text-xs font-medium text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/** Photo when one is known (Google profile), otherwise coloured initials; falls back if it fails. */
export function Avatar({
  name,
  email,
  photoUrl,
  size = 24,
  className,
}: {
  name: string;
  email?: string;
  photoUrl?: string | null;
  size?: number;
  className?: string;
}) {
  const label = name || email || '?';
  const [broken, setBroken] = React.useState<string | null>(null);
  const hue = [...label].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  if (photoUrl && broken !== photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={label}
        title={label}
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        onError={() => setBroken(photoUrl)}
        className={cn('inline-block shrink-0 rounded-full bg-muted object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, size * 0.42),
        background: `hsl(${hue} 45% 88%)`,
        color: `hsl(${hue} 45% 28%)`,
      }}
    >
      {initialsOf(name, email)}
    </span>
  );
}

function initialsOf(name: string, email?: string): string {
  const src = name?.trim() || email?.split('@')[0] || '?';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  return (
    (parts[0]?.[0] ?? '?') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')
  ).toUpperCase();
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse-soft rounded bg-muted', className)} />;
}
